import { useState, useEffect, useCallback } from 'react';
import { InvestigationScope, Investigation } from '../../../types';
import { useInvestigationService } from '../services/investigationService';

interface UseCreateInvestigationFormOptions {
  isOpen: boolean;
  customerId: string;
  onClose: () => void;
  onInvestigationCreated: (investigation: Investigation) => void;
}

/**
 * Creating a case: the AWS accounts it covers. No agent is chosen here: the server records the
 * investigation agent a Prompt Studio admin set (PO 2026-10-28).
 *
 * The playbook used to be chosen here, before anyone knew what the case was, in the dialog
 * operators skip past. It is chosen from the case itself now, for as long as the case is open,
 * so this form and its template-variable machinery went with it.
 */
export function useCreateInvestigationForm({
  isOpen,
  customerId,
  onClose,
  onInvestigationCreated,
}: UseCreateInvestigationFormOptions) {
  const investigationService = useInvestigationService();

  const [scope, setScope] = useState<InvestigationScope[]>([]);
  const [showScopeModal, setShowScopeModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setScope([]);
      setError(null);
      setLoading(false);
    }
  }, [isOpen]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // No title: the aggregate defaults to 'Untitled Investigation', the exact string the
      // agent's naming tool treats as "not named yet", so the case names itself from the first
      // message rather than keeping a placeholder forever.
      const investigation = await investigationService.createInvestigation(customerId, { scope });
      onInvestigationCreated(investigation);
      setScope([]);
      setLoading(false);
      onClose();
    } catch (err) {
      console.error('Failed to create investigation:', err);
      setError('Failed to create investigation. Please try again.');
      setLoading(false);
    }
  }, [investigationService, customerId, scope, onInvestigationCreated, onClose]);

  const handleClose = useCallback(() => {
    setScope([]);
    setError(null);
    setLoading(false);
    onClose();
  }, [onClose]);

  const handleScopeSelected = useCallback((newScope: InvestigationScope[]) => {
    setScope(newScope);
    setShowScopeModal(false);
  }, []);

  const getTotalAccountsInScope = useCallback(
    (): number =>
      scope.reduce(
        (total: number, customerScope: InvestigationScope) =>
          total + (customerScope.accounts?.length || 0),
        0,
      ),
    [scope],
  );

  return {
    scope,
    showScopeModal,
    setShowScopeModal,
    loading,
    error,
    handleSubmit,
    handleClose,
    handleScopeSelected,
    getTotalAccountsInScope,
  };
}
